/** Needs attention: where every paper stands (the pipeline, left to right), then the exceptions someone has to act on. */
import { useMemo, useState } from 'react';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { Icon } from '../shared/icons.tsx';
import { Link, useRouter } from '../shared/router.tsx';
import { ErrorBox, PageHeader, Spinner, useLoad } from '../shared/ui.tsx';

interface Item {
  type: string;
  refId?: string | null;
  severity: 'critical' | 'high' | 'normal';
  title: string;
  detail: string;
  paperId?: string;
  civilosNumber?: string;
}

type Group = { key: string; severity: Item['severity']; title: string; items: Item[] };

function ExceptionGroup({ g, open, onToggle }: { g: Group; open: boolean; onToggle: () => void }) {
  const { navigate } = useRouter();
  const first = action(g.items[0]!);
  const many = g.items.length > 1;
  return (
    <>
      <tr className="clickable exc-g" onClick={() => (many ? onToggle() : first && navigate(first[0]))} aria-expanded={many ? open : undefined}>
        <td>
          <span className={`sev ${SEVERITY[g.severity][1]}`}>{SEVERITY[g.severity][0]}</span>
        </td>
        <td>
          <strong className="exc-t">{g.title}</strong>
          {!many && <div className="muted small">{g.items[0]!.detail}</div>}
        </td>
        <td className="num">{g.items.length}</td>
        <td className="nowrap">
          {many ? (
            <button type="button" className="btn link sm" onClick={(e) => (e.stopPropagation(), onToggle())}>
              {open ? 'Hide' : `Show ${g.items.length}`}
            </button>
          ) : first ? (
            <Link to={first[0]} className="mono">
              {first[1]}
            </Link>
          ) : (
            '—'
          )}
        </td>
      </tr>
      {many &&
        open &&
        g.items.map((i, k) => {
          const a = action(i);
          return (
            <tr key={k} className="clickable exc-i" onClick={() => a && navigate(a[0])}>
              <td />
              <td className="muted" colSpan={2}>
                {i.detail}
              </td>
              <td className="nowrap">{a ? <Link to={a[0]} className="mono">{a[1]}</Link> : '—'}</td>
            </tr>
          );
        })}
    </>
  );
}

/** The order a paper moves through the office. Each stage opens that queue. */
const PIPELINE: [string, string, string][] = [
  ['review', 'Awaiting review', 'Intake to check and approve'],
  ['ready', 'Ready to assign', 'Approved, not with a deputy'],
  ['assigned', 'In the field', 'With a deputy'],
  ['return', 'Awaiting return', 'Affidavit to the court'],
  ['completed', 'Completed', 'Returned or closed'],
];
const SEVERITY: Record<Item['severity'], [string, string]> = { critical: ['Critical', 'bad'], high: ['High', 'warn'], normal: ['Normal', 'muted'] };

function action(i: Item): [string, string] | null {
  if (i.paperId) return [`/papers/${i.paperId}`, i.civilosNumber ?? 'Open'];
  if (i.type === 'door_hanger_unmatched') return ['/door-hangers', 'Match it'];
  if (i.type === 'portal_submission') return [`/scanai/batches/${i.refId}`, 'Review'];
  if (i.type === 'transfer_incoming' || i.type === 'transfer_back') return [`/transfers/${i.refId}`, 'Open'];
  if (i.type === 'setouts_ready') return ['/ejectments', 'Schedule'];
  if (i.type?.startsWith('evidence_')) return [`/evidence/${i.refId}`, 'Open'];
  return null;
}

export function Dashboard() {
  const { can } = useAuth();
  const { navigate } = useRouter();
  const att = useLoad(() => api.get<{ items: Item[] }>('/api/attention'));
  const counts = useLoad(() => api.get<{ counts: Record<string, number> }>('/api/papers/tab-counts'));
  const items = att.data?.items ?? [];
  const bySev = (s: Item['severity']) => items.filter((i) => i.severity === s).length;
  const [openKey, setOpenKey] = useState<string | null>(null);
  // Same issue on many papers reads as one line with a count; open it to see each one.
  const groups = useMemo(() => {
    const m = new Map<string, Group>();
    for (const i of items) {
      const key = `${i.severity}|${i.title.replace(/\d+ day\(s\)/, 'N day(s)')}`;
      const g = m.get(key) ?? { key, severity: i.severity, title: i.title.replace(/^\d+ day\(s\) to cutoff/, 'Close to the service cutoff'), items: [] };
      g.items.push(i);
      m.set(key, g);
    }
    const rank = { critical: 0, high: 1, normal: 2 };
    return [...m.values()].sort((a, b) => rank[a.severity] - rank[b.severity] || b.items.length - a.items.length);
  }, [items]);
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  return (
    <div className="stack">
      <PageHeader
        crumb="Civil process"
        title="Needs attention"
        sub={today}
        actions={
          <>
            <button className="btn" onClick={() => (att.reload(), counts.reload())}>
              Refresh
            </button>
            {can('papers.intake') && (
              <button className="btn primary" onClick={() => navigate('/papers/new')}>
                <Icon name="plus" size={16} /> New intake
              </button>
            )}
          </>
        }
      />
      <nav className="pipe" aria-label="Papers by stage">
        {PIPELINE.map(([tab, label, hint]) => (
          <Link key={tab} to={`/papers?tab=${tab}`} className="pipe-step">
            <span className="pipe-l">{label}</span>
            <span className="pipe-n">{counts.data ? counts.data.counts[tab] ?? 0 : '–'}</span>
            <span className="pipe-h">{hint}</span>
          </Link>
        ))}
      </nav>
      {counts.data && (counts.data.counts.recalled ?? 0) > 0 && (
        <div className="alert warn">
          {counts.data.counts.recalled} recalled paper{counts.data.counts.recalled === 1 ? ' is' : 's are'} still out. <Link to="/papers?tab=recalled">See recalled papers</Link>
        </div>
      )}
      <section className="card flush">
        <header className="card-h">
          <h2>Exceptions</h2>
          {items.length > 0 && (
            <span className="exc-sum">
              {(['critical', 'high', 'normal'] as const)
                .filter((s) => bySev(s))
                .map((s) => (
                  <span key={s} className={`sev ${SEVERITY[s][1]}`}>
                    {bySev(s)} {SEVERITY[s][0].toLowerCase()}
                  </span>
                ))}
            </span>
          )}
        </header>
        {att.loading && <Spinner />}
        <ErrorBox error={att.error} />
        {att.data && items.length === 0 && (
          <div className="empty-state">
            <Icon name="check" size={28} />
            <strong>Nothing needs attention</strong>
            <span className="muted">Problems with papers, transfers, door hangers and evidence show up here.</span>
          </div>
        )}
        {items.length > 0 && (
          <div className="table-wrap">
            <table className="table exc">
              <thead>
                <tr>
                  <th>Severity</th>
                  <th>Issue</th>
                  <th className="num">Count</th>
                  <th>Records</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <ExceptionGroup key={g.key} g={g} open={g.items.length === 1 || openKey === g.key} onToggle={() => setOpenKey(openKey === g.key ? null : g.key)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
